<template>
  <section class="customer-carousel" :aria-label="label" data-page-swipe-ignore>
    <div
      ref="track"
      class="customer-carousel__track"
      :class="{ 'is-dragging': dragging }"
      tabindex="0"
      :aria-label="`${label}. Scroll to explore.`"
      @scroll.passive="updateEdges"
      @keydown="onKeydown"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="finishDrag"
      @pointercancel="finishDrag"
      @lostpointercapture="onLostPointerCapture"
      @click.capture="onClick"
      @dragstart.prevent
    >
      <div
        v-for="(slide, index) in items"
        :key="slide.id || index"
        class="customer-carousel__slide"
      >
        <slot name="slide" :slide="slide" :index="index" />
      </div>
    </div>
    <div v-if="items.length > 1" class="customer-carousel__controls">
      <button
        type="button"
        :disabled="atStart"
        :aria-label="`Previous ${label.toLowerCase()}`"
        @click="move(-1)"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m14 6-6 6 6 6" />
        </svg>
      </button>
      <button
        type="button"
        :disabled="atEnd"
        :aria-label="`Next ${label.toLowerCase()}`"
        @click="move(1)"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m10 6 6 6-6 6" />
        </svg>
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps({
  label: { type: String, required: true },
  items: { type: Array as () => Array<Record<string, any>>, default: () => [] },
});
const track = ref<HTMLElement | null>(null);
const atStart = ref(true);
const atEnd = ref(false);
const dragging = ref(false);
let observer: ResizeObserver | undefined;
let pointerId: number | null = null;
let startX = 0;
let startScroll = 0;
let suppressClick = false;
let lastX = 0;
let lastMoveAt = 0;
let velocity = 0;
let momentumFrame = 0;

const updateEdges = () => {
  const el = track.value;
  if (!el) return;
  atStart.value = el.scrollLeft <= 2;
  atEnd.value = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
};
const behavior = (): ScrollBehavior =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? "auto"
    : "smooth";
const move = (direction: number) => {
  const el = track.value;
  if (!el) return;
  const first = el.firstElementChild as HTMLElement | null;
  const distance =
    (first?.offsetWidth || el.clientWidth) +
    parseFloat(getComputedStyle(el).gap || "0");
  el.scrollBy({ left: direction * distance, behavior: behavior() });
};
const onKeydown = (event: KeyboardEvent) => {
  if (event.target !== track.value) return;
  if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
    event.preventDefault();
    move(event.key === "ArrowLeft" ? -1 : 1);
  } else if (event.key === "Home" || event.key === "End") {
    event.preventDefault();
    track.value?.scrollTo({
      left: event.key === "Home" ? 0 : track.value.scrollWidth,
      behavior: behavior(),
    });
  }
};
const onPointerDown = (event: PointerEvent) => {
  suppressClick = false;
  // Touch and trackpads keep the browser's native momentum and vertical scrolling.
  if (event.pointerType !== "mouse" || event.button !== 0 || !track.value)
    return;
  pointerId = event.pointerId;
  startX = event.clientX;
  startScroll = track.value.scrollLeft;
  lastX = event.clientX;
  lastMoveAt = performance.now();
  velocity = 0;
  cancelAnimationFrame(momentumFrame);
};
const onPointerMove = (event: PointerEvent) => {
  if (pointerId !== event.pointerId || !track.value) return;
  if (!event.buttons) {
    finishDrag(event);
    return;
  }
  const delta = event.clientX - startX;
  if (Math.abs(delta) < 6 && !dragging.value) return;
  if (!dragging.value) {
    dragging.value = true;
    track.value.setPointerCapture(event.pointerId);
  }
  suppressClick = true;
  const now = performance.now();
  const elapsed = Math.max(now - lastMoveAt, 1);
  const instantaneousVelocity = (lastX - event.clientX) / elapsed;
  velocity = velocity * 0.72 + instantaneousVelocity * 0.28;
  lastX = event.clientX;
  lastMoveAt = now;
  track.value.scrollLeft = startScroll - delta;
};
const startMomentum = () => {
  const el = track.value;
  if (!el || Math.abs(velocity) < 0.08) return;

  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (reduceMotion) return;

  let previousTime = performance.now();
  const friction = 0.93;

  const step = (now: number) => {
    const elapsed = Math.min(now - previousTime, 32);
    previousTime = now;
    const previousScroll = el.scrollLeft;
    el.scrollLeft += velocity * elapsed;
    velocity *= Math.pow(friction, elapsed / 16.67);

    const reachedEdge = Math.abs(el.scrollLeft - previousScroll) < 0.25;
    if (Math.abs(velocity) >= 0.025 && !reachedEdge) {
      momentumFrame = requestAnimationFrame(step);
    }
  };

  momentumFrame = requestAnimationFrame(step);
};
const finishDrag = (event: PointerEvent) => {
  if (pointerId !== event.pointerId) return;
  const completedDrag = dragging.value;
  if (track.value?.hasPointerCapture(event.pointerId))
    track.value.releasePointerCapture(event.pointerId);
  pointerId = null;
  dragging.value = false;
  if (completedDrag) startMomentum();
};
const onLostPointerCapture = (event: PointerEvent) => {
  if (pointerId !== event.pointerId) return;
  pointerId = null;
  dragging.value = false;
};
const onClick = (event: MouseEvent) => {
  if (suppressClick) {
    event.preventDefault();
    event.stopPropagation();
    suppressClick = false;
  }
};
onMounted(() => {
  observer = new ResizeObserver(updateEdges);
  if (track.value) observer.observe(track.value);
  updateEdges();
});
watch(
  () => props.items,
  async () => {
    await nextTick();
    updateEdges();
  }
);
onBeforeUnmount(() => {
  observer?.disconnect();
  cancelAnimationFrame(momentumFrame);
});
</script>

<style scoped lang="scss">
.customer-carousel {
  position: relative;
  min-width: 0;
}
.customer-carousel__track {
  display: flex;
  gap: var(--customer-carousel-gap, 16px);
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  cursor: grab;
  &::-webkit-scrollbar {
    display: none;
  }
  &:focus-visible {
    outline: 2px solid #101820;
    outline-offset: 4px;
  }
  &.is-dragging {
    cursor: grabbing;
    scroll-snap-type: none;
    user-select: none;
  }
}
.customer-carousel__slide {
  display: flex;
  flex: 0 0 auto;
  scroll-snap-align: start;
}
.customer-carousel__controls {
  pointer-events: none;
  position: absolute;
  inset: 0;
  button {
    pointer-events: auto;
    position: absolute;
    top: 50%;
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border: 1px solid rgba(16, 24, 32, 0.14);
    border-radius: 50%;
    background: #fff;
    color: #101820;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(16, 24, 32, 0.14);
    transform: translateY(-50%);
    transition: background-color 150ms ease, box-shadow 150ms ease,
      opacity 150ms ease, transform 150ms ease;
    &:first-child {
      left: 12px;
    }
    &:last-child {
      right: 12px;
    }
    &:hover:not(:disabled) {
      background: #f4f3f1;
      box-shadow: 0 10px 28px rgba(16, 24, 32, 0.2);
      transform: translateY(-50%) scale(1.04);
    }
    &:disabled {
      opacity: 0.34;
      pointer-events: none;
      cursor: default;
    }
    &:focus-visible {
      outline: 2px solid #101820;
      outline-offset: 3px;
    }
  }
  svg {
    width: 20px;
    height: 20px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.5;
  }
}
@media (min-width: 768px) and (pointer: fine) {
  .customer-carousel__track {
    scroll-snap-type: none;
  }
}
@media (max-width: 767px) {
  .customer-carousel__controls {
    display: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .customer-carousel__controls button {
    transition: background-color 100ms ease, opacity 100ms ease;
  }
}
</style>
